import { getLogger, type Logger } from "@expert/logging";
import ky, { type BeforeErrorHook, type BeforeRequestHook } from "ky";

import { getBackendUrls } from "@/config/getBackendUrls";

import { userCache } from "./userCache";

export function createAuthorizationBearerHook(tokenProvider: () => string | undefined): BeforeRequestHook {
    return ({ headers }) => {
        if (headers.has("Authorization")) return;
        const accessToken = tokenProvider();
        if (!accessToken) {
            throw new Error("Access token doesn’t exist");
        }
        headers.set("Authorization", `Bearer ${accessToken}`);
    };
}

export function createCorrelationIdHook(correlationId?: string): BeforeRequestHook {
    return ({ headers }) => {
        if (correlationId) headers.set("x-ewp-correlation-id", correlationId);
    };
}

export function createErrorLoggingHook(logger: Logger): BeforeErrorHook {
    return (error) => {
        const {
            request: { method, url },
            response: { status, statusText },
        } = error;
        logger.warn({ method, url, status, statusText }, "received an HTTP Error");
        return error;
    };
}

export const getSharedApiClient = (correlationId?: string) => {
    return ky.create({
        prefixUrl: getBackendUrls().apiBaseUrl,
        timeout: 30_000,
        hooks: {
            beforeRequest: [
                createAuthorizationBearerHook(() => userCache.ssoAccessToken),
                createCorrelationIdHook(correlationId),
            ],
            beforeError: [createErrorLoggingHook(getLogger({ module: "api" }))],
        },
    });
};
